// Klient-Portal-Einstieg (G3). Oeffentlich, kein Sachbearbeiter-Auth-Gate.
//
// Ablauf beim Laden:
//  1. `?token=` in der URL? → einloesen (Redeem), Session speichern, Branding
//     anwenden, Token aus der URL/History entfernen.
//  2. Sonst: existierende Session aus dem letzten Besuch nutzen.
//  3. Weder noch → Hinweis-Seite ("Link vom Steuerberater nutzen").

import { useEffect, useRef, useState } from "react";

import { Loader } from "@/components/Loader";

import { redeem } from "./api";
import { applyBranding } from "./branding";
import { KlientUploadPage } from "./KlientUploadPage";
import {
  getKlientSession,
  setKlientSession,
  type KlientBranding,
} from "./session";

import "./klient.css";

type Status = "loading" | "ready" | "error" | "no-access";

export function KlientApp() {
  const [status, setStatus] = useState<Status>("loading");
  const [branding, setBranding] = useState<KlientBranding | null>(null);
  // Guard gegen den doppelten Effect-Lauf in React.StrictMode (dev) — der
  // Einmal-Token darf NICHT zweimal eingeloest werden.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = new URLSearchParams(window.location.search).get("token");

    function activate(b: KlientBranding) {
      setBranding(b);
      applyBranding(b);
      // Token aus URL/History tilgen (soll nicht in Bookmark/Verlauf landen).
      window.history.replaceState({}, "", "/klient/upload");
      setStatus("ready");
    }

    async function init() {
      const existing = getKlientSession();

      if (!token) {
        if (existing) {
          setBranding(existing.branding);
          applyBranding(existing.branding);
          setStatus("ready");
        } else {
          setStatus("no-access");
        }
        return;
      }

      try {
        const session = await redeem(token);
        setKlientSession(session);
        activate(session.branding);
      } catch {
        // Redeem fehlgeschlagen — falls noch eine gueltige Session da ist
        // (alter Link erneut geoeffnet), die nutzen, sonst Fehler zeigen.
        if (existing) {
          activate(existing.branding);
        } else {
          setStatus("error");
        }
      }
    }

    void init();
  }, []);

  if (status === "loading") {
    return (
      <div className="klient-shell klient-center">
        <Loader label="Zugang wird geprüft…" />
      </div>
    );
  }

  if (status === "ready") {
    return <KlientUploadPage branding={branding} />;
  }

  return (
    <div className="klient-shell klient-center">
      <div className="klient-card klient-hinweis">
        <h1>
          {status === "error"
            ? "Link ungültig oder abgelaufen"
            : "Kein Zugang"}
        </h1>
        <p>
          {status === "error"
            ? "Dieser Upload-Link ist nicht mehr gültig. Bitte fordere bei deinem Steuerberater einen neuen Link an."
            : "Bitte nutze den persönlichen Upload-Link, den dir dein Steuerberater geschickt hat."}
        </p>
      </div>
    </div>
  );
}
